import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRenameJob, type ProcessingJobList } from "@/lib/api";
import { setProjectMeta } from "@/lib/projectMeta";
import { getProjectName } from "@/lib/projectName";

interface RenameProjectDialogProps {
  job: ProcessingJobList;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RenameProjectDialog({ job, open, onOpenChange }: RenameProjectDialogProps) {
  const [name, setName] = useState("");
  const { mutate, isPending, error } = useRenameJob();

  useEffect(() => {
    if (open) setName(getProjectName(job));
  }, [open, job]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed || isPending) return;
    mutate(
      { jobId: job.id, title: trimmed },
      {
        onSuccess: () => {
          setProjectMeta(job.id, { customTitle: trimmed, titleEditedByUser: true });
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!isPending) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename project</DialogTitle>
        </DialogHeader>
        <input
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") onOpenChange(false);
          }}
          className="w-full px-3 py-2 text-sm rounded-lg border bg-background outline-none focus:ring-1 focus:ring-ring"
        />
        {error && <p className="text-xs text-destructive">{(error as Error).message}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending || !name.trim()}>
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
